import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ShieldCheck, ShieldOff, Loader2, KeyRound, Copy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Embeds the 2FA setup / disable controls inside Client Settings.
 * Reads the user's current totp_enabled flag from /api/auth/me.
 */
export default function TwoFactorSection() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  // Setup state
  const [setup, setSetup] = useState(null); // {secret, uri, qr}
  const [code, setCode] = useState('');
  const [enabling, setEnabling] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  // Disable state
  const [password, setPassword] = useState('');
  const [disabling, setDisabling] = useState(false);

  const refresh = async () => {
    try {
      const me = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setEnabled(!!me.data.totp_enabled);
    } catch (_) {} // eslint-disable-line no-empty
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const startSetup = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/auth/2fa/setup`, {}, { withCredentials: true });
      setSetup(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start 2FA setup');
    }
  };

  const confirmEnable = async () => {
    setEnabling(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/2fa/enable`, { code }, { withCredentials: true });
      setRecoveryCodes(res.data.recovery_codes || []);
      setSetup(null);
      setCode('');
      setEnabled(true);
      toast.success('Two-factor enabled - write down your recovery codes!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setEnabling(false);
    }
  };

  const disable = async () => {
    setDisabling(true);
    try {
      await axios.post(`${API_URL}/api/auth/2fa/disable`, { password }, { withCredentials: true });
      setEnabled(false);
      setPassword('');
      setRecoveryCodes(null);
      toast.success('Two-factor disabled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not disable 2FA');
    } finally {
      setDisabling(false);
    }
  };

  const copyAll = () => {
    if (!recoveryCodes) return;
    navigator.clipboard.writeText(recoveryCodes.join('\n'));
    toast.success('Recovery codes copied to clipboard');
  };

  if (loading) return null;

  return (
    <section className="bg-white border border-[#E2E8F0] rounded-xl p-6" data-testid="settings-2fa-section">
      <div className="flex items-center gap-2 mb-4">
        <KeyRound size={16} className="text-[#1D4ED8]" />
        <h2 className="font-bold text-[#0A0F1C]">Two-Factor Authentication</h2>
        {enabled ? (
          <span className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-emerald-700" data-testid="settings-2fa-status-on">
            <ShieldCheck size={12} /> Enabled
          </span>
        ) : (
          <span className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-[#94A3B8]" data-testid="settings-2fa-status-off">
            <ShieldOff size={12} /> Not enabled
          </span>
        )}
      </div>

      <p className="text-sm text-[#475569] mb-4">
        Add an extra step at login. Scan the QR with Google Authenticator, 1Password, or Authy.
        You'll get 10 one-time recovery codes in case you lose your device.
      </p>

      {!enabled && !setup && (
        <Button onClick={startSetup} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" data-testid="settings-2fa-start-btn">
          Enable two-factor
        </Button>
      )}

      {setup && (
        <div className="space-y-4" data-testid="settings-2fa-setup">
          <div className="flex items-start gap-5 flex-wrap">
            <img src={setup.qr} alt="2FA QR" className="w-40 h-40 border border-[#E2E8F0] rounded-lg" data-testid="settings-2fa-qr" />
            <div className="space-y-2 max-w-sm">
              <p className="text-sm text-[#475569]">Scan the QR with your authenticator app, then enter the 6-digit code below.</p>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Can't scan? Enter this secret manually:</label>
                <code className="text-xs font-mono bg-[#F8FAFC] border border-[#E2E8F0] rounded px-2 py-1 break-all">{setup.secret}</code>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 max-w-sm">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123 456"
              className="font-mono tracking-widest text-lg"
              data-testid="settings-2fa-code"
            />
            <Button onClick={confirmEnable} disabled={enabling || code.length !== 6} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="settings-2fa-confirm-btn">
              {enabling ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Verifying…</> : 'Confirm & enable'}
            </Button>
          </div>
        </div>
      )}

      {recoveryCodes && (
        <div className="mt-5 border border-amber-200 bg-amber-50 rounded-xl p-4" data-testid="settings-2fa-recovery">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-bold text-amber-900">Save these recovery codes</div>
            <Button size="sm" variant="outline" onClick={copyAll}><Copy size={12} className="mr-1.5" /> Copy</Button>
          </div>
          <p className="text-xs text-amber-800 mb-3">Each code can be used once if you lose your device. We won't show them again.</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 font-mono text-xs">
            {recoveryCodes.map((c, i) => <code key={i} className="bg-white border border-amber-200 rounded px-2 py-1 text-center" data-testid={`recovery-code-${i}`}>{c}</code>)}
          </div>
        </div>
      )}

      {enabled && !setup && (
        <div className="mt-4 space-y-2 max-w-sm" data-testid="settings-2fa-disable-block">
          <p className="text-xs text-[#475569]">Disabling 2FA removes the extra step at login. Confirm with your current password.</p>
          <div className="flex items-center gap-2">
            <Input
              type="password"
              placeholder="Current password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="settings-2fa-disable-password"
              autoComplete="current-password"
            />
            <Button onClick={disable} disabled={disabling || !password} variant="destructive" data-testid="settings-2fa-disable-btn">
              {disabling ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Disabling…</> : 'Disable 2FA'}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
